const EASE_OPEN = "power3.out";
const EASE_CLOSE = "power2.inOut";
const EASE_PANEL = "power2.inOut";
const DURATION_OPEN = 0.45;
const DURATION_CLOSE = 0.35;
const DURATION_PANEL = 0.4;

const SELECTORS = {
  backdrop: "[data-offcanvas-backdrop]",
  close: "[data-offcanvas-close]",
  back: "[data-offcanvas-back]",
  parentItem: "[data-parent-item]",
  panel: "[data-panel]",
  panelsWrapper: "[data-panels]",
  panelWrapper: ".offcanvas__panel-wrapper",
  focusable:
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
};

class OffcanvasMenu extends HTMLElement {
  #isOpen = false;
  #isAnimating = false;
  #activePanelName = "root";
  #panelHistory = [];
  #lastFocusedEl = null;
  #panelsByName = new Map();

  #els = {};
  #handlers = {};

  constructor() {
    super();
    this.name = this.dataset.offcanvasMenu || "default";
  }

  connectedCallback() {
    this.#cacheElements();
    this.#bindEvents();
    this.#primeGsapState();
  }

  disconnectedCallback() {
    this.#unbindEvents();
  }

  /* ---------------------------------------------------------------
   * Setup
   * ------------------------------------------------------------- */

  #cacheElements() {
    this.#els.backdrop = this.querySelector(SELECTORS.backdrop);
    this.#els.panelWrapper = this.querySelector(SELECTORS.panelWrapper);
    this.#els.panelsWrapper = this.querySelector(SELECTORS.panelsWrapper);
    this.#els.panels = Array.from(this.querySelectorAll(SELECTORS.panel));

    this.#els.panels.forEach((panel) => {
      this.#panelsByName.set(panel.dataset.panel, panel);
    });
  }

  #bindEvents() {
    this.#handlers.onSelfClick = this.#handleSelfClick.bind(this);
    this.#handlers.onDocClick = this.#handleDocumentClick.bind(this);
    this.#handlers.onKeydown = this.#handleKeydown.bind(this);

    // Internal interactions (close / backdrop / back / drill-down)
    this.addEventListener("click", this.#handlers.onSelfClick);

    // External triggers can live anywhere in the document.
    document.addEventListener("click", this.#handlers.onDocClick);

    // Escape key + focus trap, only meaningful while open.
    document.addEventListener("keydown", this.#handlers.onKeydown);
  }

  #unbindEvents() {
    this.removeEventListener("click", this.#handlers.onSelfClick);
    document.removeEventListener("click", this.#handlers.onDocClick);
    document.removeEventListener("keydown", this.#handlers.onKeydown);
  }

  #primeGsapState() {
    // Ensure GPU-accelerated transform properties are set up front
    // so the first animation doesn't trigger layout thrashing.
    gsap.set(this.#els.backdrop, { opacity: 0 });
    gsap.set(this.#els.panelWrapper, { xPercent: -100, force3D: true });

    this.#els.panels.forEach((panel) => {
      const isRoot = panel.dataset.panel === "root";
      gsap.set(panel, {
        xPercent: isRoot ? 0 : 100,
        force3D: true,
        visibility: isRoot ? "visible" : "hidden",
      });
    });
  }

  /* ---------------------------------------------------------------
   * Event handling (delegated, no per-item listeners)
   * ------------------------------------------------------------- */

  #handleSelfClick(event) {
    if (event.target.closest(SELECTORS.close)) {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.target.closest(SELECTORS.backdrop)) {
      this.close();
      return;
    }

    const backBtn = event.target.closest(SELECTORS.back);
    if (backBtn) {
      event.preventDefault();
      this.#goToPanel(backBtn.dataset.targetPanel || "root", "backward");
      return;
    }

    const parentBtn = event.target.closest(SELECTORS.parentItem);
    if (parentBtn && parentBtn.dataset.targetPanel) {
      event.preventDefault();
      this.#goToPanel(parentBtn.dataset.targetPanel, "forward");
    }
  }

  #handleDocumentClick(event) {
    const trigger = event.target.closest("[data-offcanvas-trigger]");
    if (!trigger) return;

    const targetName = trigger.dataset.offcanvasTrigger || "default";
    if (targetName !== this.name) return;

    event.preventDefault();
    this.#lastFocusedEl = trigger;
    this.#isOpen ? this.close() : this.open();
  }

  #handleKeydown(event) {
    if (!this.#isOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === "Tab") {
      this.#trapFocus(event);
    }
  }

  /* ---------------------------------------------------------------
   * Public API
   * ------------------------------------------------------------- */

  open() {
    if (this.#isOpen || this.#isAnimating) return;
    this.#isAnimating = true;
    this.#isOpen = true;

    this.removeAttribute("hidden");
    this.classList.add("is-open");
    this.setAttribute("aria-hidden", "false");
    this.#syncTriggerState(true);

    this.#lastFocusedEl = this.#lastFocusedEl || document.activeElement;

    const tl = gsap.timeline({
      defaults: { ease: EASE_OPEN, duration: DURATION_OPEN },
      onComplete: () => {
        this.#isAnimating = false;
        this.#focusFirstElement();
      },
    });

    tl.to(this.#els.backdrop, { opacity: 1 }, 0);
    tl.to(this.#els.panelWrapper, { xPercent: 0 }, 0);

    document.documentElement.setAttribute("scroll-lock", "");
  }

  close() {
    if (!this.#isOpen || this.#isAnimating) return;
    this.#isAnimating = true;

    const tl = gsap.timeline({
      defaults: { ease: EASE_CLOSE, duration: DURATION_CLOSE },
      onComplete: () => {
        this.#isOpen = false;
        this.#isAnimating = false;
        this.classList.remove("is-open");
        this.setAttribute("aria-hidden", "true");
        this.#syncTriggerState(false);
        this.#restoreFocus();
        document.documentElement.removeAttribute("scroll-lock");
        this.setAttribute("hidden", "");

        // Reset to root panel after the close animation so the next
        // open always starts from the top level, without a visible jump.
        if (this.#activePanelName !== "root") {
          this.#resetPanelsInstant();
        }
      },
    });

    tl.to(this.#els.backdrop, { opacity: 0 }, 0);
    tl.to(this.#els.panelWrapper, { xPercent: -100 }, 0);
  }

  /* ---------------------------------------------------------------
   * Panel navigation
   * ------------------------------------------------------------- */

  #goToPanel(targetName, direction) {
    if (this.#isAnimating || targetName === this.#activePanelName) return;

    const fromPanel = this.#panelsByName.get(this.#activePanelName);
    const toPanel = this.#panelsByName.get(targetName);
    if (!toPanel) return;

    this.#isAnimating = true;

    if (direction === "forward") {
      this.#panelHistory.push(this.#activePanelName);
    } else {
      this.#panelHistory.pop();
    }

    const fromExit = direction === "forward" ? -100 : 100;
    const toEnter = direction === "forward" ? 100 : -100;

    gsap.set(toPanel, { xPercent: toEnter, visibility: "visible" });
    toPanel.setAttribute("aria-hidden", "false");
    toPanel.classList.add("is-active");

    const tl = gsap.timeline({
      defaults: { ease: EASE_PANEL, duration: DURATION_PANEL },
      onComplete: () => {
        if (fromPanel) {
          fromPanel.setAttribute("aria-hidden", "true");
          fromPanel.classList.remove("is-active");
          gsap.set(fromPanel, { visibility: "hidden" });
        }
        this.#activePanelName = targetName;
        this.#isAnimating = false;
        this.#focusFirstElement(toPanel);
      },
    });

    if (fromPanel) {
      tl.to(fromPanel, { xPercent: fromExit }, 0);
    }
    tl.to(toPanel, { xPercent: 0 }, 0);
  }

  #resetPanelsInstant() {
    const rootPanel = this.#panelsByName.get("root");
    this.#panelsByName.forEach((panel, name) => {
      const isRoot = name === "root";
      gsap.set(panel, {
        xPercent: isRoot ? 0 : 100,
        visibility: isRoot ? "visible" : "hidden",
      });
      panel.classList.toggle("is-active", isRoot);
      panel.setAttribute("aria-hidden", isRoot ? "false" : "true");
    });
    this.#activePanelName = "root";
    this.#panelHistory = [];
  }

  /* ---------------------------------------------------------------
   * Accessibility helpers
   * ------------------------------------------------------------- */

  #syncTriggerState(isOpen) {
    document
      .querySelectorAll(
        `[data-offcanvas-trigger="${this.name}"], [data-offcanvas-trigger=""]`,
      )
      .forEach((trigger) =>
        trigger.setAttribute("aria-expanded", String(isOpen)),
      );
  }

  #getFocusableInScope() {
    const scope = [this.#els.panelWrapper];
    return scope
      .flatMap((root) => Array.from(root.querySelectorAll(SELECTORS.focusable)))
      .filter((el) => {
        const panel = el.closest(SELECTORS.panel);
        // Only elements in the header chrome or the active panel count.
        return (
          !panel ||
          panel.classList.contains("is-active") ||
          panel.dataset.panel === this.#activePanelName
        );
      });
  }

  #focusFirstElement(scopeEl) {
    const root = scopeEl || this.#els.panelWrapper;
    const target = root.querySelector(SELECTORS.focusable);
    target?.focus();
  }

  #restoreFocus() {
    this.#lastFocusedEl?.focus?.();
    this.#lastFocusedEl = null;
  }

  #trapFocus(event) {
    const focusable = this.#getFocusableInScope();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get("offcanvas-menu")) {
  customElements.define("offcanvas-menu", OffcanvasMenu);
}
